import { z } from 'zod';
import { uuidSchema } from './common.js';
import { calculationBasisSchema } from './financials.js';
import { projectedUsePhaseKgco2eqSchema } from './use-phase.js';

/**
 * Scenario sensitivity tornado — Zod DTOs (SIL-299, W3,
 * financials-hifi-flow-spec.md slice 4 "swing on net").
 *
 * Backs `GET /api/client/scenarios/:id/sensitivity`
 * (apps/api/src/routes/client/scenarios.ts). One-at-a-time perturbation of
 * a fixed, plain-array-literal set of 4 attributes over the SAME pure
 * `scenarioEngine` kernel `/compare` uses — see
 * `packages/shared/src/scenario-sensitivity.ts` for the compute seam and
 * `apps/api/src/lib/scenario-sensitivity.ts` for the DB resolution
 * (resolves cohort aggregates ONCE, never inside the per-attribute loop).
 */

/** The fixed tornado attributes — no registry framing, a closed enum
 * matching `SENSITIVITY_ATTRIBUTES`'s literal `id`s. `pue` added SIL-346
 * (T8): the one attribute that is a genuine "electricity input" shared by
 * both the dollar and use-phase carbon math — see
 * `SENSITIVITY_PUE_BOUND_PCT`'s docstring. */
export const sensitivityAttributeIdSchema = z.enum([
  'refresh_street_price',
  'power_kwh',
  'fmv_band',
  'residual_recovery',
  'pue',
]);
export type SensitivityAttributeId = z.infer<typeof sensitivityAttributeIdSchema>;

/**
 * One tornado row. `applicable: false` always carries a non-null `reason`
 * (spec: "a 0 swing must never read as insensitive") — `lowNetUsd`/
 * `highNetUsd`/`swingUsd` are null exactly when `applicable` is false, never
 * a disguised zero.
 */
export const sensitivityAttributeResultSchema = z.object({
  id: sensitivityAttributeIdSchema,
  label: z.string(),
  applicable: z.boolean(),
  reason: z.string().nullable(),
  /** Horizon-end net-position delta (scenario vs baseline) at the
   * scenario's UNPERTURBED assumptions — the tornado's center line, shared
   * by every attribute row. */
  baseNetUsd: z.coerce.number(),
  /** Horizon-end net-position delta with this attribute perturbed to its
   * low bound, every other input held at its base value. Null iff
   * `applicable` is false. */
  lowNetUsd: z.coerce.number().nullable(),
  /** Same, at the high bound. */
  highNetUsd: z.coerce.number().nullable(),
  /** `highNetUsd - lowNetUsd` — signed (a negative value means the "high"
   * bound produces the SMALLER net position, e.g. a higher $/kWh cost).
   * Null iff `applicable` is false. */
  swingUsd: z.coerce.number().nullable(),
  /**
   * SIL-346 (T8) — the SAME tornado row's use-phase carbon figures, always
   * present (never a second, separately-shaped response) so a consumer
   * cannot render dollars and carbon as if they came from unrelated models.
   * `baseCarbonKgco2e` is shown even when `applicable` is false (the center
   * line is attribute-independent, same posture as `baseNetUsd`).
   * `low`/`high`/`swing` are null iff `applicable` is false.
   *
   * Only the `pue` attribute moves this figure — `pue` is the one
   * "electricity input" this tornado perturbs that also feeds the use-phase
   * carbon math (`applyPue`); every other attribute (including `power_kwh`,
   * a $/kWh PRICE with no physical carbon effect) reports
   * `low == high == base` (an explicit zero swing, never a disguised null).
   */
  baseCarbonKgco2e: projectedUsePhaseKgco2eqSchema,
  lowCarbonKgco2e: projectedUsePhaseKgco2eqSchema.nullable(),
  highCarbonKgco2e: projectedUsePhaseKgco2eqSchema.nullable(),
  /** `highCarbonKgco2e - lowCarbonKgco2e`, same sign convention as `swingUsd`. */
  swingKgco2e: z.coerce.number().nullable(),
  basis: calculationBasisSchema,
});
export type SensitivityAttributeResult = z.infer<typeof sensitivityAttributeResultSchema>;

/** Response for `GET /scenarios/:id/sensitivity` — `.max(6)` shapes for a
 * future 5th/6th attribute (spec: "response shaped for future max 6
 * attributes") without widening the contract itself yet. */
export const scenarioSensitivityResponseSchema = z.object({
  scenarioId: uuidSchema,
  horizonMonths: z.number().int(),
  generatedAt: z.string(),
  attributes: z.array(sensitivityAttributeResultSchema).max(6),
});
export type ScenarioSensitivityResponse = z.infer<typeof scenarioSensitivityResponseSchema>;
